import React, { useEffect, useState } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import FlikLogo from '../../components/Logo/FlikLogo';
import ActivityTypeIcon from '../../activities/ActivityTypeIcon';
import { resolveActivityType, getActivityStats } from '../../activities/activityTypes';
import { getActivity } from '../../api/client';
import SenderismoDetail from './SenderismoDetail';
import EscaladaDetail from './EscaladaDetail';
import CiclismoDetail from './CiclismoDetail';
import BarranquismoDetail from './BarranquismoDetail';
import FerrataDetail from './FerrataDetail';
import EspeleologiaDetail from './EspeleologiaDetail';
import './ActivityDetailPage.css';

// Candidate keys for the shared (type-agnostic) fields. Backend field keys are
// admin-defined, so each one is resolved defensively — the first present wins.
export const FIELD_KEYS = {
  description: ['description', 'descripcion', 'desc', 'summary', 'resumen'],
  image: ['image', 'imageUrl', 'photo', 'foto', 'imagen', 'cover', 'portada'],
  location: ['location', 'place', 'lugar', 'ubicacion', 'zona', 'area', 'municipio'],
  region: ['region', 'provincia', 'comarca', 'zone'],
  altitude: ['altitude', 'altitud', 'elevation', 'cota', 'maxAltitude', 'cotaMaxima'],
  status: ['status', 'estado'],
  gpx: ['gpx', 'gpxUrl', 'track'],
};

/** First non-empty value among `keys` on the values object, or undefined. */
export function pick(values, keys) {
  for (const k of keys) {
    const v = values?.[k];
    if (v !== undefined && v !== null && v !== '') return v;
  }
  return undefined;
}

/** True when a value reads as an affirmative boolean. */
export function isTruthy(v) {
  if (typeof v === 'boolean') return v;
  const s = String(v).trim().toLowerCase();
  return s === 'true' || s === 'sí' || s === 'si' || s === '1' || s === 'yes';
}

// Per-type detail layouts. Types without a bespoke design fall back to the
// generic layout below. Add one entry per type as its design lands.
const DETAIL_BY_TYPE = {
  senderismo: SenderismoDetail,
  escalada: EscaladaDetail,
  ciclismo: CiclismoDetail,
  barranquismo: BarranquismoDetail,
  ferrata: FerrataDetail,
  espeleologia: EspeleologiaDetail,
};

/**
 * Activity detail page (route "/actividad/:id"). Loads the activity, then hands
 * off to the layout for its type (e.g. Senderismo) or a generic fallback. Any
 * field that isn't present in `values` is simply omitted; the weather forecast
 * has no data source yet, so it renders a placeholder.
 */
function ActivityDetailPage() {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  // Use the activity passed via navigation state for an instant render, then
  // still fetch by id so a direct link / refresh works on its own.
  const [activity, setActivity] = useState(location.state?.activity || null);
  const [loading, setLoading] = useState(!location.state?.activity);

  useEffect(() => {
    let alive = true;
    getActivity(id)
      .then((a) => {
        if (alive && a) setActivity(a);
      })
      .catch(() => {
        // Keep any state-passed activity; the render falls back when we have none.
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [id]);

  if (loading) {
    return <div className="Detail Detail--msg">Cargando actividad…</div>;
  }
  if (!activity) {
    return (
      <div className="Detail Detail--msg">
        <p>No hemos podido cargar esta actividad.</p>
        <button type="button" className="Detail__back" onClick={() => navigate('/')}>
          ← Volver al mapa
        </button>
      </div>
    );
  }

  const type = resolveActivityType(activity.activityType);
  const Layout = DETAIL_BY_TYPE[type.key] || GenericDetail;
  return <Layout activity={activity} />;
}

/* ------------------------------------------------------------------------- *
 *  Shared shell pieces — reused by every per-type layout.
 * ------------------------------------------------------------------------- */

/** Top bar: logo, type, place · altitude, status, and any extra right cell. */
export function DetailTopBar({ activity, rightCell }) {
  const navigate = useNavigate();
  const type = resolveActivityType(activity.activityType);
  const values = activity.values || {};
  const place = pick(values, FIELD_KEYS.location);
  const altitude = pick(values, FIELD_KEYS.altitude);
  const status = pick(values, FIELD_KEYS.status);

  return (
    <header className="Detail__bar">
      <button
        type="button"
        className="Detail__logo"
        onClick={() => navigate('/')}
        aria-label="Volver al mapa"
      >
        <FlikLogo height={18} />
      </button>
      <span className="Detail__barCell Detail__barCell--type">
        <ActivityTypeIcon activityType={activity.activityType} size={14} />
        {type.label.toUpperCase()}
      </span>
      {(place || altitude) && (
        <span className="Detail__barCell">
          {[place && String(place).toUpperCase(), altitude && `${altitude} M`]
            .filter(Boolean)
            .join(' · ')}
        </span>
      )}
      <span className="Detail__barCell Detail__barCell--fill">
        {status ? String(status).toUpperCase() : ''}
      </span>
      {rightCell}
    </header>
  );
}

/** Hero image (or placeholder) with a back chip and optional extra chips. */
export function DetailHero({ activity, chips }) {
  const navigate = useNavigate();
  const values = activity.values || {};
  const image = pick(values, FIELD_KEYS.image);
  return (
    <div className="Detail__hero">
      {image ? (
        <img className="Detail__heroImg" src={image} alt={activity.name} />
      ) : (
        <div className="Detail__heroPlaceholder" aria-hidden="true">
          <ActivityTypeIcon activityType={activity.activityType} size={40} />
          <span>Sin imagen</span>
        </div>
      )}
      <div className="Detail__heroChips">
        <button type="button" className="Detail__chip Detail__chip--back" onClick={() => navigate('/')}>
          ← Volver al mapa
        </button>
        {chips}
      </div>
    </div>
  );
}

/** Overlapping cream title sheet with eyebrow, title and optional actions. */
export function DetailTitleSheet({ eyebrow, title, actions }) {
  return (
    <div className="Detail__titleSheet flik-cut-xl">
      <div className="Detail__titleText">
        {eyebrow && <span className="Detail__eyebrow">{eyebrow}</span>}
        <h1 className="Detail__title">{title}</h1>
      </div>
      {actions && <div className="Detail__titleActions">{actions}</div>}
    </div>
  );
}

/** Weather forecast placeholder — no data source yet. */
export function WeatherPlaceholder() {
  return (
    <section className="Detail__section">
      <div className="Detail__sectionLabel Detail__sectionLabel--gold">PARTE METEOROLÓGICO</div>
      <div className="Detail__weather">
        {['−2 DÍAS', 'HOY', '+4 DÍAS'].map((d) => (
          <div key={d} className="Detail__weatherCell">
            <span className="Detail__weatherDay">{d}</span>
            <span className="Detail__weatherDash">—°/—°</span>
          </div>
        ))}
      </div>
      <p className="Detail__weatherNote">Previsión no disponible todavía.</p>
    </section>
  );
}

/* ------------------------------------------------------------------------- *
 *  Generic fallback layout for types without a bespoke design yet.
 * ------------------------------------------------------------------------- */

function GenericDetail({ activity }) {
  const type = resolveActivityType(activity.activityType);
  const values = activity.values || {};
  const description = pick(values, FIELD_KEYS.description);
  const place = pick(values, FIELD_KEYS.location);
  const gpx = pick(values, FIELD_KEYS.gpx);

  const allStats = getActivityStats(activity);
  const mideStat = allStats.find((s) => s.kind === 'mide' && s.present);
  const gridStats = allStats.filter((s) => s !== mideStat && s.present);
  const mideParts = mideStat ? String(mideStat.value).split('-') : [];

  return (
    <div className="Detail">
      <DetailTopBar activity={activity} />
      <DetailHero activity={activity} />
      <DetailTitleSheet
        eyebrow={[place && String(place).toUpperCase(), type.label.toUpperCase()]
          .filter(Boolean)
          .join(' · ')}
        title={activity.name}
      />
      <div className="Detail__body">
        <div className="Detail__col">
          {description && (
            <section className="Detail__section">
              <div className="Detail__sectionLabel">DESCRIPCIÓN</div>
              <p className="Detail__desc">{description}</p>
            </section>
          )}
          {mideStat && <MideBlock parts={mideParts} />}
        </div>
        <div className="Detail__col">
          {gridStats.length > 0 && (
            <div className="Detail__stats">
              {gridStats.map((stat) => (
                <div key={stat.unit} className="Detail__stat">
                  <span className="Detail__statLabel">{stat.unit.toUpperCase()}</span>
                  <span className="Detail__statVal">
                    {stat.kind === 'bool' ? (stat.value ? 'Sí' : 'No') : stat.value}
                  </span>
                </div>
              ))}
            </div>
          )}
          <WeatherPlaceholder />
          {gpx && (
            <a className="Detail__gpx flik-cut-md" href={gpx} download>
              <span>SIGUIENTE PASO</span>
              <strong>DESCARGAR GPX</strong>
            </a>
          )}
        </div>
      </div>
    </div>
  );
}

const MIDE_LABELS = ['MEDIO', 'ITINERARIO', 'DESPLAZ.', 'ESFUERZO'];

/** MIDE breakdown block (four levels), shared by layouts that show it. */
export function MideBlock({ parts }) {
  return (
    <div className="Detail__mide flik-cut-lg">
      <div className="Detail__mideHead">
        <span>DIFICULTAD · MIDE</span>
        <span>{parts.join(' · ')}</span>
      </div>
      <div className="Detail__mideGrid">
        {MIDE_LABELS.map((label, i) => (
          <div key={label} className="Detail__mideCell">
            <span className="Detail__mideVal">{parts[i] ?? '?'}</span>
            <span className="Detail__mideLabel">{label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export default ActivityDetailPage;
